import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { QueryKey } from "../../utils/api";
import client from "../../api";

type UpdateFormspecItemParams<TMetadata = unknown> = {
  item_uuid: string;
  metadata: TMetadata;
};

export interface UpdateConditionParams {
  item_uuid: string;
  metadata: {
    cellConditionId: number;
    item_tree: Record<string, any>;
  };
}

export interface UpdateCellComponentParams {
  item_uuid: string;
  metadata: {
    cellComponentId: number;
    item_tree: Record<string, any>;
  };
}

/**
 * Generic: update a FormSpec item with the provided metadata payload.
 * Under the hood this is a PUT to `formspec/items/:item_uuid`.
 */
export const useUpdateItem = <
  TMetadata extends Record<string, any> = Record<string, any>,
  TResponse = any
>(
  mutationOptions?: Partial<
    UseMutationOptions<TResponse, Error, UpdateFormspecItemParams<TMetadata>>
  >
) => {
  return useMutation<TResponse, Error, UpdateFormspecItemParams<TMetadata>>({
    mutationFn: async ({ item_uuid, metadata }) => {
      // client.put expects `body: Object`; metadata is generic so cast for compatibility
      return await client.put(
        `formspec/items/${item_uuid}`,
        metadata as Object
      );
    },
    ...mutationOptions,
  });
};

/**
 * Wrapper: update a condition-backed FormSpec item.
 * (Kept for backward compatibility / readability at call sites.)
 */
export const useUpdateCondition = (
  mutationOptions?: Partial<
    UseMutationOptions<any, Error, UpdateConditionParams>
  >
) => {
  return useUpdateItem<UpdateConditionParams["metadata"], any>(
    mutationOptions as any
  );
};

/**
 * Wrapper: update a cell-component-backed FormSpec item.
 * The backend can interpret `metadata.cellComponentId` to map the update back to Oak/meta.
 */
export const useUpdateCellComponent = (
  mutationOptions?: Partial<
    UseMutationOptions<any, Error, UpdateCellComponentParams>
  >
) => {
  return useUpdateItem<UpdateCellComponentParams["metadata"], any>(
    mutationOptions as any
  );
};

/*
 * Create a new condition with FormSpec integration
 */
export const useCreateCondition = () => {
  const { exp_id } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      conditionData: NewConditionData & { exp_id: number }
    ) => {
      return await client.post(`meta/cell-conditions/formspec`, {
        exp_id: conditionData.exp_id,
        name: conditionData.name,
        template_uuid: conditionData.template_uuid,
        base_item_uuid: conditionData.base_item_uuid,
        scale: conditionData.scale,
      });
    },
    onSuccess: () => {
      // Invalidate and refetch conditions when a new one is created
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Conditions, exp_id],
      });
    },
  });
};

/**
 * Add replicates to an existing condition
 */
export const useAddReplicates = () => {
  const { exp_id } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conditionId,
      currentNumberOfReplicates,
      numberOfReplicates,
    }: {
      conditionId: number;
      currentNumberOfReplicates: number;
      numberOfReplicates: number;
    }) => {
      const newReplicateCount = currentNumberOfReplicates + numberOfReplicates;

      // Update the condition with the new replicate count
      return await client.put(`meta/cell-conditions/${conditionId}`, {
        replicates: newReplicateCount,
      });
    },
    onSuccess: () => {
      // Invalidate and refetch conditions when replicates are added
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Conditions, exp_id],
      });
    },
  });
};

/**
 * Create staged cells for one or more conditions in a single request.
 *
 * Used by the "submit" variant of ModifyConditionsModal. Each selected
 * condition carries its target replicate count and (required) channel pool;
 * the submission details (priority/location/date/notes) are shared across all
 * conditions.
 */
export interface CreateCellsPayload {
  conditions: { cellConditionId: number; replicates: number; pool: string }[];
  planned_start_date: string | null;
  priority: number | null;
  location: string | null;
  notes: string | null;
}

export const useCreateCells = (
  mutationOptions?: Partial<UseMutationOptions<any, Error, CreateCellsPayload>>
) => {
  const { exp_id } = useParams();
  const queryClient = useQueryClient();
  const { onSuccess, ...restMutationOptions } = mutationOptions ?? {};

  return useMutation<any, Error, CreateCellsPayload>({
    mutationFn: async ({
      conditions,
      planned_start_date,
      priority,
      location,
      notes,
    }: CreateCellsPayload) => {
      return await client.post(
        `meta/experiments/${exp_id}/conditions/create-cells`,
        {
          conditions,
          planned_start_date,
          priority,
          location,
          notes,
        }
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Conditions, exp_id],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WorkOrders, exp_id],
      });
      onSuccess?.(data, variables, context);
    },
    ...restMutationOptions,
  });
};

export const useModifyCondition = () => {
  const { exp_id } = useParams();
  return useMutation<
    ConditionModification,
    Error,
    { conditionId: number; status: "modifying" | "cancelled" | "submitted" }
  >({
    mutationFn: async ({ conditionId, status }) => {
      const response = await client.post(
        `meta/experiments/${exp_id}/conditions/modify`,
        {
          cell_condition_id: conditionId,
          status,
        }
      );
      return response.data;
    },
  });
};

export interface CommitConditionPayload {
  condition_id: number;
  planned_start_date: string | null;
  priority: number | null;
  location: string | null;
  notes: string | null;
}
